/**
 * Revokes the current session's FusionAuth refresh token on user-initiated
 * logout (API-4797), so a stale refresh token can't keep minting access
 * tokens after the terminal signs out.
 *
 * `revoke_fusionauth_sso=true` additionally revokes the FusionAuth SSO
 * session token server-side — belt-and-suspenders alongside the browser
 * `/oauth2/logout` redirect (see `buildLogoutUrl` in `authCodeFlow.ts`).
 *
 * `identity_id` is intentionally omitted — the endpoint infers the identity
 * from the bearer access token. `tenant_id` is also omitted — the
 * `application_id` alone is sufficient for FusionAuth to resolve the tenant.
 */

import { fetchWithNetworkReporting } from './accountApi';

export interface RevokeRefreshTokensParams {
    /** FusionAuth application ID (`VITE_OAUTH_CLIENT_ID`) — used as the path param. */
    applicationId: string;
    /** Bearer access token identifying the session whose refresh token is being revoked. */
    accessToken: string;
    /** The current session's refresh token — only this token is revoked. */
    refreshToken: string;
    /** Base API origin, e.g. `env.apiBaseUrl`. */
    apiBaseUrl: string;
}

/**
 * DELETE {apiBaseUrl}/api/v2/auth/refresh/{applicationId}?refresh_token=...&revoke_fusionauth_sso=true
 *
 * Returns `true` on success, `false` on a non-2xx response. Never throws for
 * HTTP failures — network errors (e.g. offline) still reject, matching
 * `fetchWithNetworkReporting`'s contract. Callers should treat this as
 * best-effort and swallow any failure so logout is never blocked.
 */
export async function revokeRefreshTokens(
    params: RevokeRefreshTokensParams
): Promise<boolean> {
    const url = new URL(
        `/api/v2/auth/refresh/${params.applicationId}`,
        params.apiBaseUrl
    );
    url.searchParams.set('refresh_token', params.refreshToken);
    url.searchParams.set('revoke_fusionauth_sso', 'true');

    const res = await fetchWithNetworkReporting(url.toString(), {
        method: 'DELETE',
        headers: {
            Authorization: `Bearer ${params.accessToken}`
        }
    });

    return res.ok;
}
